'use client';

import { c as _c } from "react/compiler-runtime";
import { getSavingsCalculation } from '@/api/generated/model-catalog/model-catalog';
import type { SavingsCalculationResponse } from '@/api/generated/model';
import { useQuery } from '@tanstack/react-query';
import { AI_PROFICIENCY_PAGE_QUERY_OPTIONS, getAiProficiencyTimeRangeQueryKey } from '@/domains/pages/ai-proficiency/query-options';
import type { SavingsCalculationRow } from '@/domains/pages/model-savings/types';
import { buildModelSavingsTopicFilter, type ModelSavingsTasksQueryScope } from '@/domains/pages/model-savings/utils/model-savings-api';
import { normalizeTierGroupName } from '@/domains/pages/model-savings/utils/model-savings-tier-names';
import { useMemo } from 'react';
type UseDrawerCalculationParams = {
  open: boolean;
  queryScope: ModelSavingsTasksQueryScope | null;
  topicName?: string;
};
export function mapSavingsCalculationRows(response: SavingsCalculationResponse | undefined): SavingsCalculationRow[] {
  if (!response?.rows?.length) return [];
  return response.rows.flatMap(row => {
    const tier = normalizeTierGroupName(row.tier);
    if (!tier) return [];
    return [{
      modelName: row.model_name,
      provider: row.provider,
      modality: row.modality,
      tier,
      actualSpend: row.actual_spend ?? 0,
      optimalSpend: row.optimal_spend ?? 0,
      actualMixPercent: row.actual_mix_percent ?? 0,
      optimalMixPercent: row.optimal_mix_percent ?? 0
    }];
  });
}
export function useDrawerCalculation(t0) {
  const $ = _c(27);
  const {
    open,
    queryScope,
    topicName
  } = t0;
  let t1;
  if ($[0] !== queryScope) {
    t1 = queryScope ? getAiProficiencyTimeRangeQueryKey(queryScope.timeRange) : null;
    $[0] = queryScope;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const timeRangeQueryKey = t1;
  let t2;
  if ($[2] !== queryScope?.scopeFilters || $[3] !== topicName) {
    t2 = () => {
      const filters = [...(queryScope?.scopeFilters ?? [])];
      if (topicName) {
        filters.push(buildModelSavingsTopicFilter(topicName));
      }
      return filters;
    };
    $[2] = queryScope?.scopeFilters;
    $[3] = topicName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  queryScope?.scopeFilters;
  let t3;
  if ($[5] !== t2) {
    t3 = t2();
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const filtersV2 = t3;
  const enabled = open && queryScope != null;
  const t4 = queryScope?.projectId;
  const t5 = topicName ?? "scope";
  let t6;
  if ($[7] !== filtersV2 || $[8] !== t4 || $[9] !== t5 || $[10] !== timeRangeQueryKey) {
    t6 = ["savings-calculation", t4, timeRangeQueryKey, filtersV2, t5];
    $[7] = filtersV2;
    $[8] = t4;
    $[9] = t5;
    $[10] = timeRangeQueryKey;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== filtersV2 || $[13] !== queryScope) {
    t7 = t8 => {
      const {
        signal
      } = t8;
      return getSavingsCalculation({
        project_id: queryScope.projectId,
        time_range: queryScope.timeRange,
        filters: [],
        filters_v2: filtersV2
      }, {
        signal
      });
    };
    $[12] = filtersV2;
    $[13] = queryScope;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== enabled || $[16] !== t6 || $[17] !== t7) {
    t8 = {
      queryKey: t6,
      queryFn: t7,
      enabled,
      ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
    };
    $[15] = enabled;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const query = useQuery(t8);
  let t9;
  if ($[19] !== query.data) {
    t9 = mapSavingsCalculationRows(query.data);
    $[19] = query.data;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const calculationRows = t9;
  const t10 = query.data?.total_actual_spend ?? 0;
  const t11 = query.data?.total_optimal_spend ?? 0;
  const t12 = enabled && query.isLoading;
  let t13;
  if ($[21] !== calculationRows || $[22] !== query.isError || $[23] !== t10 || $[24] !== t11 || $[25] !== t12) {
    t13 = {
      calculationRows,
      totalActualSpend: t10,
      totalOptimalSpend: t11,
      isLoading: t12,
      isError: query.isError
    };
    $[21] = calculationRows;
    $[22] = query.isError;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}